import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { inject, Service } from '@angular/core';
import { ApiUrlService } from '@app/shared/services/api-url.service';
import { ToastrService } from 'ngx-toastr';
import { Observable } from 'rxjs';
import { catchError, retry } from 'rxjs/operators';
import { Logger } from '@app/core/logger.service';

import { SKIP_ERROR_HANDLER } from './http-context';

const log = new Logger('ErrorHandlerInterceptor');

/**
 * Adds a default error handler to API requests.
 * Kept as a stateful service because the inaccessible-server toast has a
 * cross-request debounce window; the Angular HTTP registration is functional.
 */
@Service({ autoProvided: false })
export class ErrorHandlerInterceptor implements HttpInterceptor {
  private toastr = inject(ToastrService);
  private apiUrlService = inject(ApiUrlService);

  private static readonly INACCESSIBLE_TOAST_DEBOUNCE_MS = 25000;
  private lastInaccessibleToastAt = 0;

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (request.context.get(SKIP_ERROR_HANDLER)) {
      return next.handle(request);
    }

    return next.handle(request).pipe(
      retry(0),
      catchError((error) => this.errorHandler(request, error)),
    );
  }

  private errorHandler(request: HttpRequest<unknown>, error: HttpErrorResponse): Observable<HttpEvent<unknown>> {
    if (
      request.url.includes(this.apiUrlService.getApiUrl()) ||
      request.url.replace(/\/api\/v\d+/, '').includes(this.apiUrlService.getApiUrl())
    ) {
      if (error.status === 0) {
        this.inaccessible();
      } else if (error.status === 1001) {
        this.validationError(error);
      } else {
        this.internalServerError(error);
      }
    } else {
      log.error('Request error', error);
    }

    throw error;
  }

  private inaccessible(): void {
    const now = Date.now();
    if (now - this.lastInaccessibleToastAt < ErrorHandlerInterceptor.INACCESSIBLE_TOAST_DEBOUNCE_MS) {
      return;
    }

    this.lastInaccessibleToastAt = now;
    this.toastr.error('The server is not accessible');
  }

  private internalServerError(error: HttpErrorResponse): void {
    if (error.status === 500) {
      log.error('InternalServerError: 500', error);
    }
  }

  private validationError(error: HttpErrorResponse): void {
    if (error.error.code.internalCode === 1001) {
      this.toastr.error('A validation error occured. Please correct your input');
      log.error('A validation error occured. Please correct your input', error);
    }
  }
}

/** Functional registration for provideHttpClient(). */
export const errorHandlerInterceptor: HttpInterceptorFn = (request, next) =>
  inject(ErrorHandlerInterceptor).intercept(request, { handle: next });
